/**
 * Detects non-actionable TypeError noise from the Datadog OpenFeature browser
 * SDK's IndexedDB flags cache.
 *
 * On iOS Safari (particularly Private Browsing mode, WebViews, or devices with
 * restricted IndexedDB support), the `indexedDB.open()` request can resolve
 * with an undefined database reference. The SDK then tries to access
 * `.objectStoreNames` on it, producing:
 *   "undefined is not an object (evaluating 'e.objectStoreNames')"
 *
 * The stack trace points exclusively into the minified
 * `@datadog/openfeature-browser` bundle (`indexeddb-flags-cache.js`). This
 * error is not actionable from application code — it's an SDK-internal
 * failure that degrades gracefully (the provider falls back to in-memory
 * caching).
 */
export function isIndexeddbCacheNoise(
  message: string | undefined,
  stack: string | undefined,
): boolean {
  if (!message) {
    return false;
  }

  if (!message.includes("objectStoreNames")) {
    return false;
  }

  const isSafariUndefinedAccess =
    message.includes("undefined is not an object") ||
    message.includes("null is not an object");

  if (!isSafariUndefinedAccess) {
    return false;
  }

  if (!stack || stack === `TypeError: ${message}`) {
    return true;
  }

  if (_hasOnlyDatadogSdkFrames(stack)) {
    return true;
  }

  const frames = stack.split("\n").filter((l) => l.trim().startsWith("at "));
  return frames.length === 0;
}

function _hasOnlyDatadogSdkFrames(stack: string): boolean {
  const frames = stack.split("\n").filter((l) => l.trim().startsWith("at "));
  if (frames.length === 0) {
    return false;
  }
  return frames.every(
    (frame) =>
      frame.includes("indexeddb-flags-cache") ||
      frame.includes("openfeature-browser") ||
      frame.includes("@datadog") ||
      _isGenericMinifiedChunkFrame(frame),
  );
}

function _isGenericMinifiedChunkFrame(frame: string): boolean {
  if (!frame.includes("_next/static/chunks/")) {
    return false;
  }
  const hasSourceFilePath =
    frame.includes("/src/") ||
    frame.includes("/app/") ||
    frame.includes("/lib/") ||
    frame.includes("/components/");
  return !hasSourceFilePath;
}
